import './applyVehicleSelectionPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import VehicleImagePlaceholder from '../../../assets/vehicle-placeholder.png';
import { CircularProgressFullScreen } from '../../../components/shared_new/CircularProgressFullScreen/CircularProgressFullScreen';
import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { RadioCardGroup } from '../../../components/shared_new/RadioCardGroup/RadioCardGroup';
import { RadioOptionClickEvent } from '../../../components/shared_new/RadioGroup/types/RadioOptionClickEvent';
import useAnalyticStore from '../../../store/analyticStore/useAnalyticStore';
import { HydrateSource } from '../../../store/applyStore/enums/HydrateSource';
import { getVehicleSelectionId } from '../../../store/applyStore/functions/getVehicleSelectionId';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import { getVehicleDefaultImage } from '../../../store/dashboardStore/functions/getVehicleDefaultImage';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { isValidClickEvent } from '../../../utils/component/isValidClickEvent';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    sendVehicleSelectionFoundAnalytics,
    sendVehicleSelectionMissingAnalytics,
  } = useAnalyticStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { hasVehiclesForSelection },
    isFetchingVehicleSelections,
    vehicleSelectionId,
    vehicleSelections,
    hydrate,
    setManualVehicleSelection,
    setVehicleSelectionId,
  } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      vehicleSelectionIdValue: vehicleSelectionId,
    },
  });

  const navigateEnterManually = (): void => {
    setManualVehicleSelection();
    navigate(next());
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const enterManuallyClicked = (): void => {
    sendVehicleSelectionMissingAnalytics();
    navigateEnterManually();
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    setIsInitialSelection(
      isEmpty(vehicleSelectionId) && isValidClickEvent(event),
    );
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { vehicleSelectionIdValue } = values;

    if (!vehicleSelectionIdValue) {
      return;
    }

    setVehicleSelectionId(vehicleSelectionIdValue);

    sendVehicleSelectionFoundAnalytics();

    await hydrate({
      vehicleSelectionId: vehicleSelectionIdValue,
      source: HydrateSource.VEHICLE_SELECTION,
    });

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  useEffect(() => {
    if (!hasVehiclesForSelection) {
      navigateEnterManually();
    }
  }, [hasVehiclesForSelection]);

  if (isFetchingVehicleSelections) {
    return <CircularProgressFullScreen />;
  }

  return (
    <div className="apply-vehicle-selection-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applyVehicleSelection.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applyVehicleSelection.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.applyVehicleSelection.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applyVehicleSelection.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applyVehicleSelection.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyVehicleSelection.form.h1}>
                  {t('ApplyVehicleSelectionPage.title')}
                </h1>
                <h2 data-testid={locator.pages.applyVehicleSelection.form.h2}>
                  {t('ApplyVehicleSelectionPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioCardGroup
                    defaultValue={vehicleSelectionId}
                    name="vehicleSelectionIdValue"
                    options={vehicleSelections.map((vehicle, index) => ({
                      title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
                      subtitle: `${vehicle.trim}`,
                      image:
                        getVehicleDefaultImage(vehicle) ||
                        VehicleImagePlaceholder,
                      value: getVehicleSelectionId(vehicle),
                      testId:
                        locator.pages.applyVehicleSelection.form.getRadioOption(
                          index,
                        ),
                    }))}
                    register={register}
                    testId={
                      locator.pages.applyVehicleSelection.form.radioGroupRoot
                    }
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.applyVehicleSelection.form.submitButton
                    }
                    disabled={isEmpty(vehicleSelectionId)}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplyVehicleSelectionPage.continue')}
                  </LoadingButton>

                  <p className="form-enter-manually">
                    <button
                      data-testid={
                        locator.pages.applyVehicleSelection.form
                          .enterManuallyButton
                      }
                      type="button"
                      onClick={enterManuallyClicked}
                    >
                      {t('ApplyVehicleSelectionPage.enterManually')}
                    </button>
                  </p>
                </div>
              </div>
              <footer
                data-testid={locator.pages.applyVehicleSelection.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyVehicleSelectionPage';
